import { Radio, RadioGroup, Stack, Box, Checkbox } from '@chakra-ui/react';
import { WebMercatorViewport } from '@math.gl/web-mercator';
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  // FlyToInterpolator,
  FullscreenControl,
  GeolocateControl,
  Source,
  Layer,
} from 'react-map-gl';

import Mapbox from 'components/Mapbox';
import EndLocationMarker from 'components/Mapbox/EndLocationMarker';
import StartLocationMarker from 'components/Mapbox/StartLocationMarker';
import { StatusMascot } from 'components/Mascot';
import { getBounds } from 'containers/WhoIsWorking/shared/helpers';
import { useSearchParamStore } from 'containers/WhoIsWorking/shared/useSearchParamStore';
import { useFetchWhoIsWorking } from 'hooks/whoIsWorking/useFetchWhoIsWorking';
import { getFullCoordinatesList, getTimelineSegments, Segment } from 'utils/mapUtils';

import EndLocationPopup from './EndLocationPopup';
import GeoCoordinatesPopup from './GeoCoordinatesPopup';
import StartLocationPopup from './StartLocationPopup';

import './WhoIsWorkingMap.scss';

interface IWhoIsWorkingMapProps {
  activeEmployee: any;
}

const WhoIsWorkingMap: React.FC<any> = React.forwardRef(({ activeEmployee }: IWhoIsWorkingMapProps, ref: any) => {
  const { t } = useTranslation(['whoIsWorking', 'common']);

  const [cursor, setCursor] = useState<string>('auto');
  const [viewport, setViewport] = useState(null);
  const [segments, setAllSegments] = useState<Segment[]>([]);
  const [showCoordinatesDot, setShowCoordinatesDot] = useState(false);
  const [activeStartLocationPopup, setActiveStartLocationPopup] = useState<any>(null);
  const [activeEndLocationPopup, setActiveEndLocationPopup] = useState<any>(null);
  const [activeGeoCoordinatePopup, setActiveGeoCoordinatePopup] = useState<any>(null);
  const [mapView, setMapView] = useState<'street' | 'satellite'>('street');

  const { working, group_ids } = useSearchParamStore();
  const { status, data } = useFetchWhoIsWorking({ working, group_ids }, { enabled: false });

  useEffect(() => {
    if (status === 'success' && data.payload?.length) {
      const allCoordinates = data.payload
        .filter(
          (item: any) => (item?.end_latitude && item?.end_longitude) || (item?.start_latitude && item?.start_longitude)
        )
        .map((item: any) => ({
          latitude: item?.end_latitude ?? item?.start_latitude,
          longitude: item?.end_longitude ?? item?.start_longitude,
        }));

      // Check for emppty array
      if (!allCoordinates.length) return;

      const coordinatesBounds = getBounds(allCoordinates);

      const { longitude, latitude, zoom } = new WebMercatorViewport({
        width: window.innerWidth,
        height: Math.max(600, window.innerHeight),
      }).fitBounds(coordinatesBounds, {
        padding: { left: 150, top: 150, right: 150, bottom: 100 },
        offset: [0, -100],
      });

      setViewport((viewport: any) => ({
        ...viewport,
        longitude,
        latitude,
        zoom: zoom > 20 ? 20 : zoom,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  useEffect(() => {
    const hasCoordinates = !!activeEmployee?.geo_locations?.length;

    if (!hasCoordinates) {
      setAllSegments([]);
      return;
    }

    const breaks = activeEmployee.breaks || [];

    const coordinates = getFullCoordinatesList(
      activeEmployee.geo_locations,
      activeEmployee.start_latitude,
      activeEmployee.start_longitude,
      activeEmployee.clock_in_time,
      activeEmployee.end_latitude,
      activeEmployee.end_longitude,
      activeEmployee.clock_out_time
    );

    if (!breaks.length) {
      setAllSegments([{ type: 'work', coordinates: coordinates, index: 0 }]);
      return;
    }

    const allSegments = getTimelineSegments(coordinates, breaks);

    setAllSegments(allSegments);
  }, [activeEmployee]);

  // It is bind to interactiveLayerIds
  const handleMapMouseEnter = useCallback(() => {
    setCursor('pointer');
  }, []);

  // It is bind to interactiveLayerIds
  const handleMapMouseLeave = useCallback(() => {
    setCursor('auto');
  }, []);

  const handleStartLocationMarkerClick = useCallback((userId: number, longitude: number, latitude: number) => {
    setActiveStartLocationPopup({ userId: userId, latitude: latitude, longitude: longitude });
    setActiveEndLocationPopup(null);
    setActiveGeoCoordinatePopup(null);
  }, []);

  const handleEndLocationMarkerClick = useCallback((userId: number, longitude: number, latitude: number) => {
    setActiveEndLocationPopup({ userId: userId, latitude: latitude, longitude: longitude });
    setActiveStartLocationPopup(null);
    setActiveGeoCoordinatePopup(null);
  }, []);

  const handleMapClick = useCallback((e: any) => {
    //to toggle dots popover
    const coordinatesData = e.features[0]?.properties;

    if (coordinatesData && coordinatesData?.dots) {
      setActiveGeoCoordinatePopup(coordinatesData);
      setActiveStartLocationPopup(null);
      setActiveEndLocationPopup(null);
    } else {
      setActiveGeoCoordinatePopup(null);
    }
  }, []);

  const handleMapViewChange = useCallback((nextValue: 'street' | 'satellite') => {
    setMapView(nextValue);
  }, []);

  const handleShowCoordinatesCheckboxClick = () => {
    setShowCoordinatesDot(showDot => !showDot);
    setActiveGeoCoordinatePopup(null);
  };

  if (!viewport)
    return (
      <StatusMascot
        type="main"
        description="Have your users clock in or adjust their location settings to see them on the map."
        wrapperProps={{ id: 'who-is-working-mascot' }}
      />
    );

  return (
    <Box minHeight="600px" height="100%" width="100%">
      <Mapbox
        initialViewState={viewport}
        style={{ width: '100%', height: '100%' }}
        onMouseEnter={handleMapMouseEnter}
        onMouseLeave={handleMapMouseLeave}
        onClick={handleMapClick}
        cursor={cursor}
        // onMove={(nextViewport: any) => setViewport(nextViewport)}
        mapStyle={
          mapView === 'street' ? 'mapbox://styles/mapbox/streets-v11' : 'mapbox://styles/mapbox/satellite-streets-v9'
        }
        ref={ref}
        interactiveLayerIds={showCoordinatesDot ? ['dots'] : []}
      >
        {/* map style */}
        <RadioGroup
          onChange={handleMapViewChange}
          value={mapView}
          position="absolute"
          top="10px"
          left="50"
          p="1"
          px="2"
          bg="white"
          colorScheme="primary"
          fontWeight="semibold"
          borderRadius="lg"
        >
          <Stack direction="row" cursor="pointer">
            <Radio size="sm" value="street" mr="2" data-testid="street-view-radio">
              {t('common:street')}
            </Radio>
            <Radio size="sm" value="satellite" data-testid="satellite-view-radio">
              {t('common:satellite')}
            </Radio>
          </Stack>
        </RadioGroup>
        <FullscreenControl
          data-testid="mapboxgl-ctrl-fullscreen"
          position="top-left"
          style={{
            left: 10,
            top: 10,
          }}
        />
        <GeolocateControl
          position="top-left"
          style={{
            left: 10,
            top: 50,
          }}
          positionOptions={{ enableHighAccuracy: true, timeout: 6000 }}
          trackUserLocation={true}
        />
        <Box bg="white" py="1" px="2" position="absolute" top="50px" left="50px" fontSize="sm" borderRadius="base">
          <Checkbox
            isChecked={showCoordinatesDot}
            size="sm"
            onChange={handleShowCoordinatesCheckboxClick}
            colorScheme="primary"
            data-testid="breadcrumbs-checkbox"
          >
            {t('common:showBreadcrumbs')}
          </Checkbox>
        </Box>
        {data?.payload?.map((item: any) => (
          <React.Fragment key={item.user_id}>
            {item?.end_latitude && item?.end_longitude ? (
              /* End Coordinates marker*/
              <EndLocationMarker
                latitude={item.end_latitude}
                longitude={item.end_longitude}
                userDetail={item}
                handleMarkerClick={handleEndLocationMarkerClick}
                markerBoxProps={{ zIndex: item.user_id === activeEmployee?.user_id ? 10 : 0 }}
              />
            ) : item?.start_latitude && item?.start_longitude ? (
              <EndLocationMarker
                latitude={item.start_latitude}
                longitude={item.start_longitude}
                userDetail={item}
                handleMarkerClick={handleEndLocationMarkerClick}
                markerBoxProps={{ zIndex: item.user_id === activeEmployee?.user_id ? 10 : 0 }}
              />
            ) : null}

            {/* start location point popup */}
            {activeStartLocationPopup?.userId === item.user_id && (
              <StartLocationPopup
                item={item}
                activeStartLocationPopup={activeStartLocationPopup}
                setActiveStartLocationPopup={setActiveStartLocationPopup}
              />
            )}

            {/* end location point popup */}
            {activeEndLocationPopup?.userId === item.user_id && (
              <EndLocationPopup
                item={item}
                activeEmployee={activeEmployee}
                activeEndLocationPopup={activeEndLocationPopup}
                setActiveEndLocationPopup={setActiveEndLocationPopup}
              />
            )}
          </React.Fragment>
        ))}

        {/* Show break and work segments */}
        {segments.map(segment => (
          <Source
            key={`${segment.type}-${segment.index}`}
            id={`${segment.type}-source-${segment.index}`}
            type="geojson"
            data={{
              type: 'Feature',
              properties: {},
              geometry: {
                type: 'LineString',
                coordinates: segment.coordinates.map(coordinate => [coordinate.longitude, coordinate.latitude]),
              },
            }}
          >
            <Layer
              id={`${segment.type}-layer-${segment.index}`}
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round',
              }}
              paint={{
                'line-color': '#6A71B1',
                'line-width': 4,
                ...(segment.type === 'break' && { 'line-dasharray': [1, 2] }),
              }}
            />
          </Source>
        ))}

        {/* BreadCrumbs */}
        {showCoordinatesDot ? (
          <Source
            id="dots"
            type="geojson"
            data={{
              type: 'FeatureCollection',
              // @ts-ignore
              features:
                Array.isArray(activeEmployee?.geo_locations) && activeEmployee.geo_locations.length
                  ? activeEmployee?.geo_locations
                      .filter((coordinate: any) => {
                        // filtering out start and end coordindates
                        if (
                          coordinate.longitude === activeEmployee?.start_longitude &&
                          coordinate.latitude === activeEmployee?.start_latitude
                        ) {
                          return false;
                        } else if (
                          coordinate.longitude === activeEmployee?.end_longitude &&
                          coordinate.latitude === activeEmployee?.end_latitude
                        ) {
                          return false;
                        } else {
                          return true;
                        }
                      })
                      .map((geoCoordinate: any) => {
                        return {
                          type: 'Feature',
                          properties: { ...geoCoordinate, dots: true },
                          geometry: {
                            type: 'Point',
                            coordinates: [geoCoordinate.longitude, geoCoordinate.latitude],
                          },
                        };
                      })
                  : [],
            }}
          >
            <Layer
              id="dots"
              type="circle"
              paint={{
                'circle-color': '#FF6720',
                'circle-radius': 6,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#ffffff',
              }}
            />
          </Source>
        ) : null}

        {activeEmployee ? (
          <>
            {activeEmployee?.end_latitude &&
            activeEmployee?.end_longitude &&
            activeEmployee?.start_latitude !== null &&
            activeEmployee?.start_longitude !== null ? (
              <StartLocationMarker
                latitude={activeEmployee.start_latitude}
                longitude={activeEmployee.start_longitude}
                userId={activeEmployee.user_id}
                handleMarkerClick={handleStartLocationMarkerClick}
              />
            ) : null}

            {/* geoCoordinate point popup */}
            {activeGeoCoordinatePopup && (
              <GeoCoordinatesPopup
                item={activeEmployee}
                activeGeoCoordinatePopup={activeGeoCoordinatePopup}
                setActiveGeoCoordinatePopup={setActiveGeoCoordinatePopup}
              />
            )}
          </>
        ) : null}
      </Mapbox>
    </Box>
  );
});

export default WhoIsWorkingMap;
